import NotesIcon from "@mui/icons-material/Notes";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type {ColumnDef} from "@tanstack/react-table";
import type {
    AggregationSurveyListItem,
    AggregationSurveySubmissionListItem
} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {surveyColorOf} from "crqlar-react-common/dist/utils/surveyColor.util";
import type {TFunction} from "i18next";
import {DateElement} from "../../common/uiElements/DateElement";
import {LanguagesElement} from "../../common/uiElements/LanguagesElement";
import {surveyNpsClassLabelKey} from "../../common/uiElements/SurveyNpsClassElement";
import {SurveyReviewStateElement} from "../../common/uiElements/SurveyReviewStateElement";
import {formatSurveyScore} from "../../common/uiElements/SurveyScoreElement";
import {surveyMarkdownToPlainText} from "../shared/surveyMarkdownText.util";
import SubmissionActionsCell from "./SubmissionActionsCell";
import {
    SurveyAttentionTypeChips,
    SurveySubmissionGuestCell,
    SurveySubmissionSurveyCell
} from "./SurveySubmissionCells";

type Column = ColumnDef<AggregationSurveySubmissionListItem>;

const formatScore = (score?: number | null) => (score == null ? '—' : formatSurveyScore(score));

const formatNpsClass = (t: TFunction, npsClass?: string | null) => {
    const labelKey = surveyNpsClassLabelKey(npsClass);
    return labelKey ? t(labelKey) : '—';
};

// The settings list's columns the module tab offers too, hidden until someone picks them.
const REVIEW_EXTRA_COLUMN_IDS = [
    'score',
    'npsClass',
    'language',
    'reservationLabel',
    'sectorName',
    'guestCategories',
    'reviewedAt',
    'reviewedByName'
];

interface ActionsColumnArgs {
    t: TFunction
    readOnly: boolean
    markingId: string | null
    onView: (id: string) => void
    onMarkReviewed: (submission: AggregationSurveySubmissionListItem) => void
}

// Where every row starts, on both lists: the eye opens the reply, the orange button resolves it in one click and a
// resolved one shows its check - the state needs no chip beside it. Pinned, so it stays in reach when more
// columns scroll the table sideways.
export function surveySubmissionActionsColumn({t, readOnly, markingId, onView, onMarkReviewed}: ActionsColumnArgs): Column {
    return {
        id: 'actions',
        header: t('Actions'),
        cell: ({row}) => {
            const submission = row.original;
            const busy = markingId === submission.id;

            return (
                <SubmissionActionsCell
                    submission={submission}
                    markReviewedBusy={busy}
                    markReviewedDisabled={readOnly || busy}
                    onView={() => onView(submission.id)}
                    onMarkReviewed={() => onMarkReviewed(submission)}
                />
            );
        },
        size: 104,
        enableResizing: false,
        meta: {defaultActive: true, defaultPinned: true}
    };
}

/** The settings list: one column per field, for print and export. */
export function surveySubmissionSettingsColumns(t: TFunction, actions: Column): Column[] {
    return [
        actions,
        {
            id: 'createdAt',
            header: t('Submitted'),
            accessorFn: submission => submission.createdAt,
            cell: ({row}) => <DateElement date={row.original.createdAt}/>,
            size: 190,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <DateElement date={row.original.createdAt}/>
            }
        },
        {
            id: 'survey',
            header: t('Survey'),
            accessorFn: submission => submission.surveyName ?? submission.surveyId,
            cell: ({row}) => (
                <Typography variant={'body1'} noWrap>
                    {row.original.surveyName || row.original.surveyId || '—'}
                </Typography>
            ),
            size: 240,
            meta: {
                defaultActive: true,
                grow: true,
                isServerSortable: true,
                serverSortField: 'surveyName',
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'profileName',
            header: t('Guest'),
            accessorFn: submission => submission.profileName,
            cell: ({row}) => (
                <Stack direction={'row'} alignItems={'center'} gap={0.75} sx={{minWidth: 0}}>
                    <Typography variant={'body1'} noWrap>
                        {row.original.profileName || '—'}
                    </Typography>

                    {!!row.original.reviewNotes?.trim() && (
                        <Tooltip title={surveyMarkdownToPlainText(row.original.reviewNotes)}>
                            <NotesIcon sx={{fontSize: 16, color: 'text.secondary', flexShrink: 0}}/>
                        </Tooltip>
                    )}
                </Stack>
            ),
            size: 220,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => row.original.profileName ?? '',
                printOriginalCell: row => row.original.profileName ?? ''
            }
        },
        {
            // One number per response whatever the survey asked, which is what lets a list across
            // surveys stand in for the per-question answer columns a single survey could show.
            id: 'score',
            header: t('Score'),
            accessorFn: submission => submission.score,
            cell: ({row}) => (
                <Box sx={{fontVariantNumeric: 'tabular-nums'}}>{formatScore(row.original.score)}</Box>
            ),
            size: 110,
            meta: {
                defaultActive: true,
                align: 'right',
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => formatScore(row.original.score)
            }
        },
        {
            // Beside the score, as in the response dialog.
            id: 'npsClass',
            header: t('NPS'),
            accessorFn: submission => submission.npsClass,
            cell: ({row}) => formatNpsClass(t, row.original.npsClass),
            size: 130,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => formatNpsClass(t, row.original.npsClass)
            }
        },
        {
            id: 'reviewState',
            header: t('Review state'),
            accessorFn: submission => submission.reviewState,
            cell: ({row}) => <SurveyReviewStateElement reviewState={row.original.reviewState}/>,
            size: 160,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <SurveyReviewStateElement reviewState={row.original.reviewState} asPrint/>
            }
        },
        {
            id: 'reviewedAt',
            header: t('Resolved at'),
            accessorFn: submission => submission.reviewedAt,
            cell: ({row}) => <DateElement date={row.original.reviewedAt}/>,
            size: 190,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printCell: row => <DateElement date={row.original.reviewedAt}/>
            }
        },
        {
            id: 'reviewedByName',
            header: t('Resolved by'),
            accessorFn: submission => submission.reviewedByName,
            cell: ({row}) => row.original.reviewedByName || '—',
            size: 180,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printCell: row => row.original.reviewedByName ?? ''
            }
        },
        {
            id: 'language',
            header: t('Language'),
            accessorFn: submission => submission.language,
            cell: ({row}) => (
                <LanguagesElement languageCodes={row.original.language ? [row.original.language] : undefined}/>
            ),
            size: 130,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => (
                    <LanguagesElement
                        languageCodes={row.original.language ? [row.original.language] : undefined}
                        asPrint
                    />
                )
            }
        },
        {
            id: 'reservationLabel',
            header: t('Reservation code'),
            accessorFn: submission => submission.reservationLabel,
            cell: ({row}) => row.original.reservationLabel ?? '',
            size: 180,
            meta: {defaultActive: false, isServerSortable: true, printable: true}
        },
        {
            id: 'sectorName',
            header: t('Sector'),
            accessorFn: submission => submission.sectorName,
            cell: ({row}) => row.original.sectorName ?? '',
            size: 180,
            meta: {defaultActive: false, isServerSortable: true, serverSortField: 'sectorName', printable: true}
        },
        {
            id: 'guestCategories',
            header: t('Guest categories'),
            accessorFn: submission => submission.guestCategories?.join(', '),
            cell: ({row}) => row.original.guestCategories?.join(', ') ?? '',
            size: 220,
            meta: {defaultActive: false, printable: true}
        },
        {
            id: 'surveyVersion',
            header: t('Version'),
            accessorFn: submission => submission.surveyVersion,
            cell: ({row}) => row.original.surveyVersion ?? '',
            size: 120,
            meta: {defaultActive: false, isServerSortable: true, printable: true}
        },
        {
            id: 'surveyId',
            header: t('Survey ID'),
            accessorFn: submission => submission.surveyId,
            cell: ({row}) => row.original.surveyId ?? '',
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: submission => submission.id,
            cell: ({row}) => row.original.id,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true}
        }
    ];
}

/**
 * The module tab: made for reading and resolving replies one by one, with the settings list's
 * `REVIEW_EXTRA_COLUMN_IDS` behind them, hidden. `surveysById` gives each row its survey's colour.
 */
export function surveySubmissionReviewColumns(
    t: TFunction,
    actions: Column,
    settings: Column[],
    surveysById: ReadonlyMap<string, Pick<AggregationSurveyListItem, 'id' | 'color'>>
): Column[] {
    return [
        actions,
        {
            id: 'attentionType',
            header: t('Needs attention'),
            accessorFn: submission => submission.attentionTypes,
            cell: ({row}) => <SurveyAttentionTypeChips types={row.original.attentionTypes}/>,
            // Room for both chips side by side in English; a narrower column wraps them.
            size: 290,
            meta: {defaultActive: true}
        },
        {
            id: 'createdAt',
            header: t('Date'),
            accessorFn: submission => submission.createdAt,
            cell: ({row}) => <DateElement date={row.original.createdAt}/>,
            size: 190,
            meta: {defaultActive: true, isServerSortable: true}
        },
        {
            id: 'guest',
            header: t('Guest and comment'),
            accessorFn: submission => submission.profileName,
            cell: ({row}) => <SurveySubmissionGuestCell submission={row.original}/>,
            size: 360,
            meta: {defaultActive: true, grow: true, isServerSortable: true, serverSortField: 'profileName'}
        },
        {
            id: 'survey',
            header: t('Survey'),
            accessorFn: submission => submission.surveyName ?? submission.surveyId,
            cell: ({row}) => (
                <SurveySubmissionSurveyCell
                    name={row.original.surveyName || row.original.surveyId || '—'}
                    color={surveyColorOf(surveysById.get(row.original.surveyId ?? '') ?? {id: row.original.surveyId})}
                />
            ),
            size: 240,
            meta: {defaultActive: true, isServerSortable: true, serverSortField: 'surveyName'}
        },
        ...REVIEW_EXTRA_COLUMN_IDS.flatMap(id => {
            const column = settings.find(candidate => candidate.id === id);
            return column ? [{...column, meta: {...column.meta, defaultActive: false}}] : [];
        })
    ];
}
